import Gumshoe from "gumshoejs";
import { useEffect } from "react";

export interface TOCItem {
  level: number;
  title: string;
  id: string;
  children?: TOCItem[];
}

export function generateTableOfContents(
  headings: NodeListOf<HTMLHeadingElement>
): TOCItem[] {
  const toc: TOCItem[] = [];
  const stack: TOCItem[] = [];

  for (const heading of headings) {
    const level = Number.parseInt(heading.tagName.substring(1));
    const title = heading.textContent || "";
    const id = heading.id;
    const item: TOCItem = { level, title, id };

    while (stack.length > 0 && stack[stack.length - 1].level >= level) {
      stack.pop();
    }

    if (stack.length === 0) {
      toc.push(item);
    } else {
      if (!stack[stack.length - 1].children) {
        stack[stack.length - 1].children = [];
      }
      stack[stack.length - 1].children?.push(item);
    }

    stack.push(item);
  }

  return toc;
}

export function TableOfContents({ items }: { items: TOCItem[] }) {
  useEffect(() => {
    if (items.length === 0) return;

    const spy = new Gumshoe(".toc-link", {
      nested: true,
      nestedClass: "active-parent",
      // Magic number corresponding to the height of the sticky header ("#app-header").
      // We can pass a function here, but it adds some perf overhead and minimal benefit.
      offset: 92,
    });

    return () => {
      if (spy) {
        spy.destroy();
      }
    };
  }, [items]);

  if (items.length === 0) return null;

  const renderTOC = (items: TOCItem[]) => {
    return (
      <ol className={`toc-level toc-level-${items[0].level}`}>
        {items.map((item, index) => (
          <li key={index} className={`toc-item toc-item-h${item.level}`}>
            <a
              className={`toc-link toc-link-h${item.level}`}
              href={`#${item.id}`}
            >
              {item.title}
            </a>
            {item.children && renderTOC(item.children)}
          </li>
        ))}
      </ol>
    );
  };

  return <nav className="toc">{renderTOC(items)}</nav>;
}
